import ResizableTextarea from '@/components/ResizableTextarea';
import Upload from '@/components/Upload';
import Constants from '@/Constants';
import { FileUploadData } from '@/hooks/upload/FileUpload';
import { QuizResult } from '@/models/QuizResult';
import { ApplicationState } from '@/store';
import { setFloatingCourseActionsConfiguration } from '@/store/common/actions';
import { FloatingCourseActionsBarConfiguration } from '@/store/common/types';
import { clearQuiz, getQuizResultListRequest, postQuizResultRequest } from '@/store/quiz/action';
import React, { useEffect, useRef, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Switch from 'react-switch';
import _ from 'underscore';
import { ValidationError } from 'yup';
import { formErrors, formInitialState, ResultForm, resultSchema } from './formConfig';
import QuizResultPreviewScreen from './ResultPreview';
import './style.scss';

const CreateQuizResultScreen: React.FC = () => {
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const MAX_RESULTS_PER_QUIZ = 3;
    const HIGHER_RESULT_INDEX = MAX_RESULTS_PER_QUIZ - 1;

    const { strings, success, quizResults, quizContent, colors } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        personId: state.identity.data.person.id,
        identity: state.identity.data,
        quiz: state.quiz.data.quizData.quiz,
        quizResults: state.quiz.data.quizData.quizResults,
        quizContent: state.quiz.data.quizData.quizContent,
        success: state.quiz.success,
        colors: state.appOrganization.data.colors,
    }));

    const uploadComponentRef = useRef<{ clearFileInput: Function }>(null);
    const [formState, setFormState] = useState(formInitialState);
    const { title, introduction, image, description, hint }: ResultForm = formState;
    const [{ titleError }, setFormError] = useState(formErrors);
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [onResultSwitch, setOnResultSwitch] = useState<number | undefined | 'new'>();
    const [onPrevPressed, setOnPrevPressed] = useState<boolean>(false);
    const [onNextPressed, setOnNextPressed] = useState<boolean>(false);
    const [onSavePressed, setOnSavePressed] = useState<boolean>(false);

    const [deleteableResult, setDeleteableResult] = useState<boolean>(false);
    const [hintEnabled, setHintEnabled] = useState<boolean>(false);
    const [currentResultIndex, setCurrentResultIndex] = useState<number>(0);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [imagePreviewFile, setImagePreviewFile] = useState<string>();

    useEffect(() => {
        if (id) {
            dispatch(getQuizResultListRequest(parseInt(id)));
        } else if (!id) {
            dispatch(clearQuiz());
        }
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                currentStep: 2,
                nextButtonTitle: strings.FORWARD,
                onNext: () => setOnNextPressed(true),
                onPrevious: () => setOnPrevPressed(true),
                onSave: () => setOnSavePressed(true),
                saveDisabled: false,
                previousButtonTitle: strings.BACK,
                saveButtonTitle: quizContent.id ? strings.SAVE_AND_CLOSE : strings.SAVE_TO_DRAFT,
                totalSteps: 4,
                nextDisabled: formIsValid ? false : true,
                previewDisabled: false,
                blockUi: false,
            }),
        );
    }, [strings._LANGUAGE]);

    useEffect(() => {
        if (onPrevPressed) {
            redirect();
        }
    }, [onPrevPressed]);

    const validateForm = () => {
        const existingTitles: string[] = (quizResults || []).filter((_result, index) => index !== currentResultIndex).map((r) => r.title);

        setFormError(formErrors);
        resultSchema
            .validate(formState, {
                context: {
                    hintRequired: hintEnabled,
                    existingTitles: existingTitles,
                    strings: strings,
                },
                abortEarly: false,
            })
            .then(() => setFormIsValid(true))
            .catch((err) => {
                setFormIsValid(false);
                if (err.inner) {
                    err.inner.forEach((error: ValidationError) => {
                        if (error.type === 'notSameTitle') {
                            setFormError((prevState) => ({
                                ...prevState,
                                titleError: error.message,
                            }));
                        }
                    });
                }
            });
    };

    useEffect(() => {
        disableSaveAndNavButtons(!formIsValid);
    }, [formIsValid, strings._LANGUAGE, dispatch]);

    useEffect(() => {
        if (!hintEnabled) {
            handleOnChange('hint', formInitialState.hint);
        } else {
            validateForm();
        }
    }, [hintEnabled]);

    useEffect(validateForm, [formState, hintEnabled]);

    const handleRemoveButtom = () => {
        if (isEditing) {
            //TODO deletar resultado
            alert('A exclusão ainda não implementada.');
        } else {
            setCurrentResultIndex(currentResultIndex - 1);
        }
    };

    useEffect(() => {
        if (onSavePressed || onNextPressed || onResultSwitch !== undefined) {
            saveResult();
        }
    }, [onSavePressed, onNextPressed, onResultSwitch]);

    const handleOnChange = (fieldName: string, value: string | Date | number | boolean | null | undefined) => {
        setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
    };

    useEffect(() => {
        if (quizResults && quizResults.length > currentResultIndex) {
            handleOnChange('image', quizResults[currentResultIndex].image);
        }
    }, [uploadComponentRef]);

    useEffect(() => {
        if (uploadComponentRef && uploadComponentRef.current) {
            uploadComponentRef.current.clearFileInput();
        }
        if (quizResults && quizResults.length > currentResultIndex) {
            setIsEditing(true);
            handleOnChange('title', quizResults[currentResultIndex].title);
            handleOnChange('description', quizResults[currentResultIndex].description);
            handleOnChange('image', quizResults[currentResultIndex].image);
            handleOnChange('introduction', quizResults[currentResultIndex].introduction);
            handleOnChange('hint', quizResults[currentResultIndex].hint);
            setHintEnabled(quizResults[currentResultIndex].hint.length > 0);
            setDeleteableResult(quizResults && quizResults.length > 1);
        } else {
            setIsEditing(false);
            setFormState(formInitialState);
            setImagePreviewFile(undefined);
            setHintEnabled(false);
            setDeleteableResult(quizResults && (quizResults.length > 1 || quizResults.length === 1));
        }
    }, [quizResults, currentResultIndex]);

    useEffect(() => {
        if (success) {
            redirect();
        }
    }, [success]);

    const saveResult = () => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                nextButtonTitle: strings.LOADING,
                blockUi: true,
            } as FloatingCourseActionsBarConfiguration),
        );
        const quizResultTO = ({
            id: isEditing ? quizResults[currentResultIndex].id : null,
            quizId: parseInt(id),
            title: formState.title,
            description: formState.description,
            introduction: formState.introduction,
            image: formState.image,
            hint: formState.hint,
        } as unknown) as QuizResult;
        if (isEditing) {
            if (_.isEqual(quizResults[currentResultIndex], quizResultTO)) {
                redirect();
            } else {
                //TODO send put request
                alert('A edição ainda não implementada.');
            }
        } else {
            dispatch(postQuizResultRequest(quizResultTO));
        }
    };

    const redirect = () => {
        if (onResultSwitch !== undefined) {
            dispatch(
                setFloatingCourseActionsConfiguration({
                    nextButtonTitle: strings.FORWARD,
                    blockUi: false,
                } as FloatingCourseActionsBarConfiguration),
            );
            setCurrentResultIndex(onResultSwitch === 'new' ? quizResults.length : onResultSwitch);
            setOnResultSwitch(undefined);
        }

        if (onPrevPressed) {
            history.push(`/contents/buzz/tests/${id}`);
        }

        if (onSavePressed) {
            history.push('/contents/buzz/tests');
        }

        if (onNextPressed) {
            history.push(`/contents/buzz/tests/${id}/questions`);
        }
    };

    const disableSaveAndNavButtons = (disabled: boolean): void => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                saveDisabled: disabled,
                nextDisabled: disabled,
            } as FloatingCourseActionsBarConfiguration),
        );
    };

    const handleSwitchResult = (nextIndex: number | 'new') => {
        if (currentResultIndex !== nextIndex && formIsValid) {
            setOnResultSwitch(nextIndex);
        }
    };

    return (
        <div className="create-quiz-result-container">
            <span className="create-quiz-info__warning">* {strings.REQUIRED_FIELDS}</span>
            <div className="create-quiz-result-title-desc">
                {strings.QUIZ_RESULTS_TITLE}
                <div className="create-quiz-result-desc">{strings.QUIZ_RESULTS_DESCRIPTION}</div>
            </div>
            <div className="create-quiz-result-buttons">
                {(quizResults || []).flatMap((_quizResult: QuizResult, index: number) => {
                    return (
                        <button
                            className={currentResultIndex === index ? 'active' : 'inactive'}
                            disabled={currentResultIndex === index || !formIsValid}
                            key={index}
                            onClick={() => handleSwitchResult(index)}
                        >{`${strings.RESULT} ${index + 1}`}</button>
                    );
                })}
                {!isEditing && <button className="active" disabled={true}>{`${strings.RESULT} ${currentResultIndex + 1}`}</button>}
                {quizResults && quizResults.length < MAX_RESULTS_PER_QUIZ && currentResultIndex < HIGHER_RESULT_INDEX && (
                    <button className="new-result" disabled={!formIsValid} onClick={() => handleSwitchResult('new')}>
                        + {strings.NEW_QUIZ_RESULT}
                    </button>
                )}
            </div>
            <hr></hr>
            <div className="create-quiz-result-content">
                <div className="create-quiz-result-form">
                    <div className="create-quiz-result-form-result-number">
                        {strings.RESULT} {currentResultIndex + 1}:
                        {deleteableResult && (
                            <div className="remove-result-button" onClick={handleRemoveButtom}>
                                <span className="material-icons-outlined align-middle">{Constants.icons.TRASH_CAN}</span> {strings.REMOVE_RESULT}
                            </div>
                        )}
                    </div>
                    <div className="input-section required">
                        <div className="d-flex align-items-center">
                            <h1 className="warning">{strings.TITLE}</h1>
                        </div>
                        {titleError && <h1 className="error-message">{titleError}</h1>}
                        <ResizableTextarea
                            countChars
                            maxLength={25}
                            placeholder={strings.ENTER_HERE_THE_RESULT}
                            setValue={(text: string) => handleOnChange('title', text)}
                            textAreaClassName="form-control"
                            value={title}
                        />
                    </div>
                    <div className="input-section">
                        <div className="label-with-hint">
                            <h1 className="warning">{strings.INTRODUCTION}</h1>
                            <span>
                                <OverlayTrigger
                                    overlay={<Tooltip id="evaluation_weight_hint">{strings.INTRODUCTION_TIP}</Tooltip>}
                                    placement="bottom"
                                >
                                    <i className="material-icons-outlined align-middle hint">{Constants.icons.ERROR_OUTLINE}</i>
                                </OverlayTrigger>
                            </span>
                        </div>
                        <ResizableTextarea
                            countChars
                            maxLength={40}
                            placeholder={strings.TYPE_INTRODUCTION}
                            setValue={(text: string) => handleOnChange('introduction', text)}
                            textAreaClassName="form-control"
                            value={introduction}
                        />
                    </div>
                    <div className="input-section margin-bottom-16">
                        <h1>{strings.IMAGE}</h1>

                        <Upload
                            onComplete={(e: FileUploadData) => {
                                handleOnChange('image', e.key);
                                setImagePreviewFile(e.preview);
                            }}
                            onRemove={() => {
                                handleOnChange('image', formInitialState.image);
                                setImagePreviewFile(undefined);
                            }}
                            previewFile={{ uri: image, name: image }}
                            ref={uploadComponentRef}
                            typeAccepted="IMAGE"
                        />
                    </div>
                    <div className="input-section required margin-bottom-16">
                        <h1>{strings.DESCRIPTION}</h1>
                        <ResizableTextarea
                            countChars
                            maxLength={270}
                            maxRows={4}
                            minRows={3}
                            placeholder={strings.ENTER_HERE_THE_DESCRIPTION_RESULT}
                            setValue={(text: string) => handleOnChange('description', text)}
                            textAreaClassName="form-control"
                            value={description}
                        />
                    </div>
                    <div className="input-section margin-bottom-16">
                        <div className="switch-section">
                            <Switch
                                checked={hintEnabled}
                                checkedIcon={false}
                                disabled={false}
                                handleDiameter={20}
                                height={14}
                                offColor="#d1d1d1"
                                offHandleColor="#b3b3b3"
                                onChange={() => setHintEnabled((prev) => !prev)}
                                onColor={colors[0]}
                                onHandleColor={colors[0]}
                                uncheckedIcon={false}
                                width={30}
                            />
                            <span>{strings.ADD_TIP}</span>
                        </div>
                    </div>
                    {hintEnabled && (
                        <div className="input-section required">
                            <h1 className="warning">{strings.TEXT}</h1>
                            <ResizableTextarea
                                countChars
                                maxLength={90}
                                placeholder={strings.ENTER_HERE_THE_TIP_TEXT}
                                setValue={(text: string) => handleOnChange('hint', text)}
                                textAreaClassName="form-control"
                                value={hint}
                            />
                        </div>
                    )}

                    <div className="input-section" />
                </div>
                <div className="create-quiz-result-preview">
                    <QuizResultPreviewScreen formState={formState} imageTempPath={imagePreviewFile} />
                </div>
            </div>
        </div>
    );
};

export default CreateQuizResultScreen;
